/** The picked values of an editable picker, as value-action triggers.
 *
 *  A `RemoteAutocomplete` / `Autocomplete` chip's click removes or focuses it,
 *  and these widgets expose no tag renderer, so the links a picked value offers
 *  (open it, filter a grid by it, copy it) live in this caption row right under
 *  the picker instead — the picker keeps its primary click untouched, which is
 *  the editable-picker rule from the detail-links plan (D2).
 *
 *  Renders nothing when nothing is picked, and skips any item that has no
 *  actions, so a blank field never grows a dead trigger. */
import { Typography, type SxProps, type Theme } from '@mui/material';
import type { ReactNode } from 'react';

import { ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';

export interface PickerLink {
  key: string | number;
  label: ReactNode;
  actions: ValueAction[];
}

export function PickerLinks({
  items,
  prefix,
  sx,
}: {
  items: PickerLink[];
  prefix?: string;
  /** Merged over the row's own (its 4 px top margin) — a slot's caption line takes none. */
  sx?: SxProps<Theme>;
}) {
  const shown = items.filter((it) => it.actions.length > 0);
  if (shown.length === 0) return null;
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      component="div"
      sx={[{ mt: 0.5 }, ...(Array.isArray(sx) ? sx : [sx])]}
    >
      {prefix ? `${prefix} ` : null}
      {shown.map((it, i) => (
        <Typography key={it.key} component="span" variant="caption">
          {i > 0 && ' · '}
          <ValueActions actions={it.actions}>{it.label}</ValueActions>
        </Typography>
      ))}
    </Typography>
  );
}
